import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GraduationCap, Baby, Gamepad2, Eye, BrainCircuit, Flame, ScrollText,
  Infinity as InfinityIcon, MessageCircle, ArrowLeft, Share2, Link2, Twitter, Linkedin, MessageSquare, Heart,
  Briefcase, Hammer, Landmark, Stethoscope, Users, Handshake, History,
  Crosshair, FlaskConical, Swords, Radio, Microscope, Dices, ShieldCheck, Lock,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { api } from "@/lib/api";
import { getVoterId } from "@/lib/voter";
import { CocoonMark } from "@/components/CocoonMark";

const ICONS = {
  "ia-ecole": GraduationCap, "ia-enfants": Baby, "ludification": Gamepad2, "attention": Eye,
  "par-coeur": ScrollText, "curiosite": Flame, "diplome": ScrollText, "toute-la-vie": InfinityIcon,
  "ia-formation-pro": Briefcase, "ia-metiers-manuels": Hammer, "ia-sup": Landmark, "ia-sante": Stethoscope,
  "ia-relations": Users, "ia-couple": Heart, "ia-amitie": Handshake, "ia-transmission": History,
  "ia-armement": Crosshair, "ia-proliferation": FlaskConical, "ia-ethique-combattant": Swords, "ia-dissuasion": Radio,
  "ia-recherche": Microscope, "ia-serendipite": Dices, "ia-integrite": ShieldCheck, "ia-privatisation": Lock,
};

export default function EchoTopic() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const topics = t("echo.topics", { returnObjects: true }) || [];
  const topic = Array.isArray(topics) ? topics.find((x) => x.id === id) : null;
  const [like, setLike] = useState({ likes: 0, liked: false });

  useEffect(() => {
    if (!id) return;
    const vid = getVoterId();
    api.get(`/echo/likes?voter_id=${encodeURIComponent(vid)}`).then(({ data }) => {
      const found = (data.likes || []).find((l) => l.topic_id === id);
      if (found) setLike({ likes: found.likes, liked: found.liked });
    }).catch(() => {});
  }, [id]);

  const toggleLike = async () => {
    const vid = getVoterId();
    setLike((p) => ({ likes: p.likes + (p.liked ? -1 : 1), liked: !p.liked }));
    try {
      const { data } = await api.post("/echo/like", { topic_id: id, voter_id: vid });
      setLike({ likes: data.likes, liked: data.liked });
    } catch {
      setLike((p) => ({ likes: p.likes + (p.liked ? -1 : 1), liked: !p.liked }));
    }
  };

  useEffect(() => {
    if (!topic) return;
    const prevTitle = document.title;
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    document.title = `${topic.title} · COCOON`;
    setMeta('meta[name="description"]', "content", topic.teaser);
    setMeta('meta[property="og:title"]', "content", `${topic.title} · COCOON`);
    setMeta('meta[property="og:description"]', "content", topic.teaser);
    setMeta('meta[name="twitter:title"]', "content", `${topic.title} · COCOON`);
    setMeta('meta[name="twitter:description"]', "content", topic.teaser);
    return () => { document.title = prevTitle; };
  }, [topic]);

  if (!topic) return <Navigate to="/echo" replace />;

  const Icon = ICONS[topic.id] || BrainCircuit;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = t("echo.shareText", { title: topic.title, question: topic.question });

  const discuss = () => {
    localStorage.setItem("coccon_pending_prompt", t("echo.prompt", { title: topic.title, question: topic.question }));
    navigate("/atelier");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("echo.linkCopied"));
    } catch {
      toast.success(t("echo.linkCopied"));
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: topic.title, text: shareText, url }); } catch {}
    } else {
      copyLink();
    }
  };

  const enc = encodeURIComponent;
  const twitter = `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}`;
  const whatsapp = `https://wa.me/?text=${enc(shareText + " " + url)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;

  return (
    <div className="min-h-screen bg-background text-foreground starfield">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="echotopic-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/echo" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.echo")}</Link>
            <Link to="/entrer" data-testid="echotopic-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-36 pb-24">
        <Link to="/echo" className="inline-flex items-center gap-2 text-sm text-foreground/50 hover:text-primary transition-colors mb-10" data-testid="echotopic-back">
          <ArrowLeft size={15} /> {t("echo.back")}
        </Link>

        <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} data-testid="echotopic-article">
          <Icon className="text-primary mb-6" size={44} strokeWidth={1.4} />
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-6" data-testid="echotopic-title">{topic.title}</h1>
          <p className="text-foreground/70 leading-relaxed text-lg mb-8">{topic.teaser}</p>
          <p className="font-display italic text-2xl text-primary/90 leading-snug mb-10">{topic.question}</p>

          <div className="flex flex-wrap items-center gap-3 mb-14">
            <button
              data-testid="echotopic-discuss"
              onClick={discuss}
              className="btn-frost-orange inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-base"
            >
              <MessageCircle size={18} /> {t("echo.cta")}
            </button>
            <button
              data-testid="echotopic-like"
              onClick={toggleLike}
              aria-label={t("echo.likeAria")}
              className={`inline-flex items-center gap-2 px-6 py-4 rounded-full text-sm border transition-colors ${
                like.liked ? "bg-primary/15 text-primary border-primary/40" : "text-foreground/60 border-white/10 hover:text-primary hover:border-primary/30"
              }`}
            >
              <Heart size={18} fill={like.liked ? "currentColor" : "none"} />
              <span className="tabular-nums">{like.likes}</span>
            </button>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="uppercase tracking-[0.22em] text-xs text-foreground/40 mb-4 flex items-center gap-2">
              <Share2 size={14} /> {t("echo.share")}
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={copyLink} data-testid="share-copy" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <Link2 size={15} /> {t("echo.copyLink")}
              </button>
              <a href={twitter} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <Twitter size={15} /> X
              </a>
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <MessageSquare size={15} /> WhatsApp
              </a>
              <a href={linkedin} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <Linkedin size={15} /> LinkedIn
              </a>
              <button onClick={nativeShare} data-testid="share-native" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2 sm:hidden">
                <Share2 size={15} /> {t("echo.share")}
              </button>
            </div>
          </div>
        </motion.article>
      </section>
    </div>
  );
}
